/**
 * TODO: getCollisions should take into account the extra offsets + I think page offsets are not being properly taken
 * into account everywhere. Also also I need to correct innerWidth to not include scrollbars.
 */

import { PRect } from '@components/Tooltip/Tooltip';

export interface PositionerOptions {
  xPreference?: 'left' | 'right' | 'largest';
  yPreference?: 'top' | 'bottom' | 'largest';
  alignX?: 'edge' | 'cover';
  alignY?: 'edge' | 'cover';
  bindToViewport?: boolean;
  adaptive?: boolean;
  offsetX?: number;
  offsetY?: number;
}

export interface ElementPosition {
  top: number | string;
  right: number | string;
  bottom: number | string;
  left: number | string;
}

export interface ElementCollisions {
  top: boolean;
  right: boolean;
  bottom: boolean;
  left: boolean;
}

export function getCollisions(
  targetRect: DOMRect | PRect,
  popoverRect: DOMRect | PRect,
  popoverContentHeight?: number,
  xPreference?: PositionerOptions['xPreference'],
  yPreference?: PositionerOptions['yPreference'],
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  alignX: PositionerOptions['alignX'] = 'edge',
  alignY: PositionerOptions['alignY'] = 'edge',
  offsetX = 0,
  offsetY = 0
): ElementCollisions {
  // We allow for the popover to be bound to the viewport which means that we cap it's height.
  // However we still want to know in this situation whether there is a collision based on the content
  // height and not the capped height, so we will prefer the content height.
  const popoverHeight = popoverContentHeight ?? popoverRect.height;

  const targetRectTop = alignY === 'edge' ? targetRect.top : targetRect.bottom;
  const targetRectBottom = alignY === 'edge' ? targetRect.bottom : targetRect.top;

  const collisions = {
    top: targetRectTop - popoverHeight - offsetY <= 0,
    left: targetRect.left - popoverRect.width - offsetX <= 0,
    bottom: window.innerHeight <= targetRectBottom + popoverHeight + offsetY,
    right: targetRect.right + popoverRect.width + offsetX >= window.innerWidth,
  };

  return collisions;
}

interface EdgeAnchorState {
  top: boolean;
  right: boolean;
  bottom: boolean;
  left: boolean;
}

/**
 * Get the position of the popover that best aligns with the preferences provided. If adaptive is true than
 * we will position the popover to maximise the amount of space available whilst sticking to the preferences
 * as best as we can (preferences will be ignored when they lead to a strange outcome)
 * @param targetRect
 * @param popoverRect
 * @param popoverContentHeight
 * @param xPreference
 * @param yPreference
 * @param alignX
 * @param alignY
 * @param offsetX
 * @param offsetY
 * @param adaptive
 * @returns
 */
export function getPopoverRectPosition(
  targetRect: DOMRect,
  popoverRect: DOMRect,
  popoverContentHeight: number | undefined,
  xPreference: 'left' | 'right' | 'largest',
  yPreference: 'top' | 'bottom' | 'largest',
  alignX: PositionerOptions['alignX'] = 'edge',
  alignY: PositionerOptions['alignY'] = 'edge',
  offsetX = 0,
  offsetY = 0,
  adaptive: boolean
): { x: number; y: number; edgeAnchorState: EdgeAnchorState } {
  let x = 0,
    y = 0;

  // We allow for the popover to be bound to the viewport which means that we cap it's height.
  // However we still want to know in this situation whether there is a collision based on the content
  // height and not the capped height, so we will prefer the content height.
  const popoverHeight = popoverContentHeight ?? popoverRect.height;

  const largestDirection = getLargestDirection(targetRect);

  const xPlacement = xPreference === 'largest' ? largestDirection.x : xPreference;
  const yPlacement = yPreference === 'largest' ? largestDirection.y : yPreference;

  const edgeAnchorState: EdgeAnchorState = {
    top: yPlacement === 'bottom',
    bottom: yPlacement === 'top',
    left: xPlacement === 'right',
    right: xPlacement === 'left',
  };

  // Calculate the X position based off provided preferences
  if (xPlacement === 'left' && alignX === 'edge') x = targetRect.left - popoverRect.width - offsetX;
  else if (xPlacement === 'left' && alignX === 'cover') x = targetRect.right - popoverRect.width - offsetX;
  else if (xPlacement === 'right' && alignX === 'edge') x = targetRect.right + offsetX;
  else if (xPlacement === 'right' && alignX === 'cover') x = targetRect.left + offsetX;

  // Correct the X position if we have gone beyond the screen
  if (adaptive) {
    if (x - popoverRect.width < 0 && alignX === 'edge' && largestDirection.x === 'right') {
      // If we have more space to the right of the target to accomodate the popover than do that
      x = targetRect.right + offsetX;
      edgeAnchorState.left = true;
      edgeAnchorState.right = false;
    } else if (
      x - popoverRect.width < 0 &&
      alignX === 'cover' &&
      targetRect.left >= 0 &&
      largestDirection.x === 'right'
    ) {
      // If we have more space to the right of the left edge of the target to accomodate the popover than do that
      x = targetRect.left + offsetX;
      edgeAnchorState.left = true;
      edgeAnchorState.right = false;
    } else if (
      x - popoverRect.width < 0 &&
      alignX === 'cover' &&
      targetRect.left < 0 &&
      largestDirection.x === 'right'
    ) {
      // If we can no longer align to the left edge of the target and there is more room on the right
      // then we may break the alignX preference
      x = targetRect.left + offsetX;
      edgeAnchorState.left = true;
      edgeAnchorState.right = false;
    } else if (
      x + popoverRect.width > window.innerWidth &&
      alignX === 'edge' &&
      largestDirection.x === 'left'
    ) {
      // If we have more space to the left of the target to accomodate the popover than do that
      x = targetRect.left - popoverRect.width - offsetX;
      edgeAnchorState.left = false;
      edgeAnchorState.right = true;
    } else if (
      x + popoverRect.width > window.innerWidth &&
      alignX === 'cover' &&
      targetRect.right >= window.innerWidth - x &&
      largestDirection.x === 'left'
    ) {
      // If we have more space to the left of the right edge of the target to accomodate the popover than do that
      x = targetRect.right - popoverRect.width - offsetX;
      edgeAnchorState.left = false;
      edgeAnchorState.right = true;
    } else if (
      x + popoverRect.width > window.innerWidth &&
      alignX === 'cover' &&
      targetRect.right >= window.innerWidth &&
      largestDirection.x === 'left'
    ) {
      // If we can no longer align to the right edge of the target and there is more room on the left
      // then we may break the alignX preference
      x = targetRect.left - popoverRect.width - offsetX;
      edgeAnchorState.left = false;
      edgeAnchorState.right = true;
    }
  }

  // Calculate the Y position based off provided preferences
  if (yPlacement === 'top' && alignY === 'edge') y = targetRect.top - popoverHeight - offsetY;
  else if (yPlacement === 'top' && alignY === 'cover') y = targetRect.bottom - popoverHeight - offsetY;
  else if (yPlacement === 'bottom' && alignY === 'edge') y = targetRect.bottom + offsetY;
  else if (yPlacement === 'bottom' && alignY === 'cover') y = targetRect.top + offsetY;

  // Correct the Y position if we have gone beyond the screen
  if (adaptive) {
    if (y - popoverHeight < 0 && alignY === 'edge' && largestDirection.y === 'bottom') {
      // If we have more space to the bottom of the target to accomodate the popover than do that
      y = targetRect.bottom + offsetY;
      edgeAnchorState.top = true;
      edgeAnchorState.bottom = false;
    } else if (
      y - popoverHeight < 0 &&
      alignY === 'cover' &&
      targetRect.top >= 0 &&
      largestDirection.y === 'bottom'
    ) {
      // If we have more space to the bottom of the top edge of the target to accomodate the popover than do that
      y = targetRect.top + offsetY;
      edgeAnchorState.top = true;
      edgeAnchorState.bottom = false;
    } else if (
      y - popoverHeight < 0 &&
      alignY === 'cover' &&
      targetRect.top < 0 &&
      largestDirection.y === 'bottom'
    ) {
      // If we can no longer align to the top edge of the target and there is more room on the bottom
      // then we may break the alignY preference
      y = targetRect.bottom + offsetY;
      edgeAnchorState.top = true;
      edgeAnchorState.bottom = false;
    } else if (y + popoverHeight > window.innerHeight && alignY === 'edge' && largestDirection.y === 'top') {
      // If we have more space to the top of the target to accomodate the popover than do that
      y = targetRect.top - popoverHeight - offsetY;
      edgeAnchorState.top = false;
      edgeAnchorState.bottom = true;
    } else if (
      y + popoverHeight > window.innerHeight &&
      alignY === 'cover' &&
      targetRect.bottom >= window.innerHeight - y &&
      largestDirection.y === 'top'
    ) {
      // If we have more space to the top of the bottom edge of the target to accomodate the popover than do that
      y = targetRect.bottom - popoverHeight - offsetY;
      edgeAnchorState.top = false;
      edgeAnchorState.bottom = true;
    } else if (
      y + popoverHeight > window.innerHeight &&
      alignY === 'cover' &&
      targetRect.bottom >= window.innerHeight &&
      largestDirection.y === 'top'
    ) {
      // If we can no longer align to the bottom edge of the target and there is more room on the top
      // then we may break the alignY preference
      y = targetRect.top - popoverHeight - offsetY;
      edgeAnchorState.top = false;
      edgeAnchorState.bottom = true;
    }
  }

  return { x, y, edgeAnchorState };
}

/**
 * This doesn't need to take into account the alignment or the offset since they apply equally to both directions
 * @param targetRect
 * @returns
 */
function getLargestDirection(targetRect: DOMRect): { x: 'left' | 'right'; y: 'top' | 'bottom' } {
  const distanceToTop = targetRect.top;
  const distanceToBottom = window.innerHeight - targetRect.bottom;
  const distanceToLeft = targetRect.left;
  const distanceToRight = window.innerWidth - targetRect.right;

  const largestX = distanceToLeft > distanceToRight ? 'left' : 'right';
  const largestY = distanceToBottom > distanceToTop ? 'bottom' : 'top';

  return { x: largestX, y: largestY };
}

const VIEWPORT_VERTICAL_PADDING = 8;
const VIEWPORT_HORIZONTAL_PADDING = 16;

export const positionBounded =
  (options?: PositionerOptions) =>
  (targetRect: DOMRect, popoverRect: DOMRect, popoverContentHeight = 0): Partial<ElementPosition> => {
    const {
      xPreference = 'left',
      yPreference = 'bottom',
      alignX = 'edge',
      alignY = 'edge',
      adaptive = true,
      offsetX = 0,
      offsetY = 0,
      bindToViewport = true,
    } = options || {};

    const popoverPosition = getPopoverRectPosition(
      targetRect,
      popoverRect,
      popoverContentHeight,
      xPreference,
      yPreference,
      alignX,
      alignY,
      offsetX,
      offsetY,
      adaptive
    );

    const popoverHeight = popoverContentHeight ?? popoverRect.height;

    function positionBottomMost() {
      return {
        top: popoverPosition.y + window.pageYOffset,
        bottom: -window.pageYOffset + VIEWPORT_VERTICAL_PADDING,
      };
    }

    function positionY() {
      return {
        top: popoverPosition.y + window.pageYOffset,
        bottom: 'unset',
      };
    }

    function positionTopmost() {
      return {
        top: window.pageYOffset + VIEWPORT_VERTICAL_PADDING,
        bottom: window.innerHeight - (popoverPosition.y + popoverHeight) - window.pageYOffset,
      };
    }

    function positionRightmost() {
      return {
        left: 'unset',
        right: window.pageXOffset + VIEWPORT_HORIZONTAL_PADDING,
      };
    }

    function positionX() {
      return {
        left: window.pageXOffset + popoverPosition.x,
        right: 'unset',
      };
    }

    function positionLeftmost() {
      return {
        left: window.pageXOffset + VIEWPORT_HORIZONTAL_PADDING,
        right: window.innerWidth - (popoverPosition.x + popoverRect.width) - window.pageXOffset,
      };
    }

    let verticalPos;
    let horizontalPos;

    /**
     * To position the popover we make up to 3 passes of the problem. First we figure out where the popover
     * should go in the happy case according to the user preferences. Then we calculate where the popover
     * should be moved to if the default placement is suboptimal (i.e. the popover is offscreen when it doesn't
     * need to be). Finally it does one less pass to bind the popover to the viewport (the following logic),
     * so if the popover is still off screen it will be cropped to fit the screen and will become scrollable.
     */
    if (bindToViewport && popoverPosition.x < 0 && popoverPosition.edgeAnchorState.right) {
      horizontalPos = positionLeftmost();
    } else if (
      bindToViewport &&
      popoverPosition.x + popoverRect.width > window.innerWidth &&
      popoverPosition.edgeAnchorState.left
    ) {
      horizontalPos = positionRightmost();
    } else horizontalPos = positionX();

    if (bindToViewport && popoverPosition.y < 0 && popoverPosition.edgeAnchorState.bottom) {
      verticalPos = positionTopmost();
    } else if (
      bindToViewport &&
      popoverPosition.y + popoverHeight > window.innerHeight &&
      popoverPosition.edgeAnchorState.top
    ) {
      verticalPos = positionBottomMost();
    } else verticalPos = positionY();

    return {
      ...verticalPos,
      ...horizontalPos,
    };
  };

export const positionRight =
  (options?: PositionerOptions) =>
  (targetRect: DOMRect, popoverRect: DOMRect): Partial<ElementPosition> => {
    const { bottom, top } = getCollisions(targetRect, popoverRect);
    const { offsetX = 0, offsetY = 0 } = options || {};
    const above = bottom && !top;

    return {
      left: targetRect.left + targetRect.width + offsetX,
      top: above
        ? `${targetRect.top - popoverRect.height + window.pageYOffset + offsetY}px`
        : `${targetRect.top + window.pageYOffset + offsetY}px`,
    };
  };

export const positionBottom =
  (options?: PositionerOptions) =>
  (targetRect: DOMRect, popoverRect: DOMRect): Partial<ElementPosition> => {
    const { bottom, top } = getCollisions(targetRect, popoverRect);
    const { offsetX = 0, offsetY = 0 } = options || {};
    const above = bottom && !top;

    return {
      left: targetRect.left + offsetX,
      top: above
        ? `${targetRect.top - popoverRect.height + window.pageYOffset - offsetY}px`
        : `${targetRect.top + targetRect.height + window.pageYOffset + offsetY}px`,
    };
  };

export const positionCentered =
  (options?: PositionerOptions) =>
  (targetRect: DOMRect, popoverRect: DOMRect): Partial<ElementPosition> => {
    const { offsetX = 0, offsetY = 0 } = options || {};
    const { right: collisionRight } = getCollisions(
      targetRect,
      popoverRect,
      popoverRect.height,
      'right',
      'bottom',
      'edge',
      'edge',
      offsetX + popoverRect.width / 2,
      offsetY
    );
    const SCREEN_PADDING = 2;

    return {
      left: collisionRight
        ? window.pageXOffset + window.innerWidth - SCREEN_PADDING
        : window.pageXOffset + targetRect.left + popoverRect.width / 2,
      top: `${targetRect.top + targetRect.height + window.pageYOffset + offsetY}px`,
    };
  };
